import React, { useState, useEffect } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/components/AuthProvider';
import { useCarbonProjects } from '@/hooks/useCarbonProjects';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  TreePine, 
  Plus, 
  Eye, 
  Edit, 
  Share2,
  MapPin,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Upload,
  Send
} from 'lucide-react';

const ProjectMyProjects = () => {
  const { user } = useAuth();
  const { projects, loading, refetch } = useCarbonProjects(user?.id || '');
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'verified': { icon: CheckCircle2, color: 'bg-green-100 text-green-800', label: 'Verified' },
      'under_review': { icon: Clock, color: 'bg-orange-100 text-orange-800', label: 'Under Review' },
      'pending': { icon: AlertCircle, color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      'active': { icon: CheckCircle2, color: 'bg-blue-100 text-blue-800', label: 'Active' },
      'draft': { icon: FileText, color: 'bg-gray-100 text-gray-800', label: 'Draft' }
    };
    
    const config = statusMap[status as keyof typeof statusMap] || statusMap.draft;
    const IconComponent = config.icon;
    
    return (
      <Badge className={config.color}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const handleShareProject = async (projectId: string) => {
    try {
      // Simulate sharing functionality
      toast({
        title: "Project Shared",
        description: "Project shared with verification agencies successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to share project",
        variant: "destructive"
      });
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">My Projects</h1>
              <p className="text-muted-foreground">
                Manage and track your carbon credit projects
              </p>
            </div>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Register New Project
            </Button>
          </div>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Project Overview</TabsTrigger>
            <TabsTrigger value="tracking">Status Tracking</TabsTrigger>
            <TabsTrigger value="verification">Verification</TabsTrigger>
            <TabsTrigger value="registration">Registration</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <TreePine className="w-5 h-5" />
                    Project Portfolio
                  </span>
                  <div className="flex items-center gap-4">
                    <Input
                      placeholder="Search projects..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-64"
                    />
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="under_review">Under Review</SelectItem>
                        <SelectItem value="verified">Verified</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {filteredProjects.length === 0 ? (
                  <div className="text-center py-12">
                    <TreePine className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-semibold mb-2">No Projects Found</h3>
                    <p className="text-muted-foreground mb-4">
                      {projects.length === 0 
                        ? "Start by registering your first carbon credit project" 
                        : "No projects match your current filters"
                      }
                    </p>
                    <Button>
                      <Plus className="w-4 h-4 mr-2" />
                      Register New Project
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {filteredProjects.map((project) => (
                      <Card key={project.registry_id} className="border hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-semibold">{project.name}</h3>
                                {getStatusBadge(project.status)}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {project.location}
                                </span>
                                <span>Type: {project.type}</span>
                                <span>Methodology: {project.methodology}</span>
                                {project.vintage_year && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {project.vintage_year}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-6">
                                <div>
                                  <span className="text-sm text-muted-foreground">Credits Issued:</span>
                                  <span className="ml-2 font-semibold text-green-600">
                                    {Number(project.credits_issued).toLocaleString()} tCO₂e
                                  </span>
                                </div>
                                <div>
                                  <span className="text-sm text-muted-foreground">Credits Retired:</span>
                                  <span className="ml-2 font-semibold text-blue-600">
                                    {Number(project.credits_retired).toLocaleString()} tCO₂e
                                  </span>
                                </div>
                                <div>
                                  <span className="text-sm text-muted-foreground">Available:</span>
                                  <span className="ml-2 font-semibold text-primary">
                                    {(Number(project.credits_issued) - Number(project.credits_retired)).toLocaleString()} tCO₂e
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button variant="outline" size="sm">
                                <Eye className="w-4 h-4 mr-2" />
                                View
                              </Button>
                              <Button variant="outline" size="sm">
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleShareProject(project.registry_id)}
                              >
                                <Share2 className="w-4 h-4 mr-2" />
                                Share
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tracking" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Project Status Tracking
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {filteredProjects.map((project) => (
                    <Card key={project.registry_id} className="border">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold">{project.name}</h3>
                          {getStatusBadge(project.status)}
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <span className="font-medium">Registration</span>
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Complete
                            </Badge>
                          </div>
                          
                          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <span className="font-medium">Documentation</span>
                            <Badge className={project.status === 'draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}>
                              {project.status === 'draft' ? (
                                <>
                                  <Clock className="w-3 h-3 mr-1" />
                                  In Progress
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Complete
                                </>
                              )}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <span className="font-medium">Verification</span>
                            <Badge className={
                              project.status === 'verified' ? 'bg-green-100 text-green-800' :
                              project.status === 'under_review' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {project.status === 'verified' ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Verified
                                </>
                              ) : project.status === 'under_review' ? (
                                <>
                                  <Clock className="w-3 h-3 mr-1" />
                                  Under Review
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Pending
                                </>
                              )}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <span className="font-medium">Credit Issuance</span>
                            <Badge className={Number(project.credits_issued) > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {Number(project.credits_issued) > 0 ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Issued
                                </>
                              ) : (
                                <>
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending
                                </>
                              )}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="verification" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Verification Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredProjects.map((project) => (
                    <Card key={project.registry_id} className="border">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold">{project.name}</h3>
                            <p className="text-sm text-muted-foreground">{project.location}</p>
                          </div>
                          {getStatusBadge(project.status)}
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <Button variant="outline" size="sm">
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Documents
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleShareProject(project.registry_id)}
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Share with Agencies
                          </Button>
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4 mr-2" />
                            View Progress
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="registration" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Project Registration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <Plus className="w-16 h-16 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Register New Project</h3>
                  <p className="text-muted-foreground mb-6">
                    Start the registration process for your new carbon credit project
                  </p>
                  <Button size="lg">
                    <Plus className="w-5 h-5 mr-2" />
                    Begin Registration
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default ProjectMyProjects;